
import { useState, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { handleFileUpload } from "@/lib/imageUploadUtils";
import CameraCapture from "./CameraCapture";
import ImagePreview from "./ImagePreview";
import PhotoGallery from "./PhotoGallery";
import ImageCaptureInitial from "./ImageCaptureInitial";
import ImageCaptureActions from "./ImageCaptureActions";
import CameraErrorDisplay from "./CameraErrorDisplay";
import { toast } from "sonner";

interface ImageCaptureProps {
  onImageCaptured: (image: string) => void;
  capturedImage?: string | null;
  onGenerateIdeas?: () => void;
  isGenerating?: boolean;
  userCredits?: any;
}

export function ImageCapture({ 
  onImageCaptured, 
  capturedImage: externalCapturedImage,
  onGenerateIdeas,
  isGenerating,
  userCredits
}: ImageCaptureProps) {
  const [mode, setMode] = useState<"initial" | "camera" | "preview" | "gallery">("initial");
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isImageConfirmed, setIsImageConfirmed] = useState<boolean>(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Use external captured image state if provided, otherwise use internal state
  const currentCapturedImage = externalCapturedImage || capturedImage;

  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);
      console.log("Starting camera...");
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "environment" } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
      
      // Important: Set mode AFTER successfully getting the stream
      setMode("camera");
      console.log("Camera started successfully, mode set to camera");
    } catch (err) {
      console.error("Error accessing camera:", err);
      setCameraError("Camera access denied. Please enable camera permissions.");
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      console.log("Stopping camera stream");
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const handleCapture = (imageDataUrl: string) => {
    console.log("Image captured from camera");
    setCapturedImage(imageDataUrl);
    setMode("preview");
    stopCamera();
  };

  const resetCapture = () => {
    setCapturedImage(null);
    setIsImageConfirmed(false);
    setMode("initial");
  };

  const confirmImage = () => {
    if (currentCapturedImage) {
      console.log("Image confirmed for processing");
      onImageCaptured(currentCapturedImage);
      setIsImageConfirmed(true);
      toast.success("Image ready for processing");
    } else {
      toast.error("No image available to process");
    }
  };

  const handleSelectFromGallery = (imageDataUrl: string) => {
    console.log("Image selected from gallery");
    setCapturedImage(imageDataUrl);
    setMode("preview");
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  return (
    <div className="w-full max-w-md mx-auto">
      <div 
        className={cn(
          "relative aspect-[4/5] overflow-hidden rounded-xl bg-gray-100 shadow-md mb-4 transition-all duration-300",
          mode === "initial" ? "bg-gray-50" : ""
        )}
      >
        {mode === "initial" && <ImageCaptureInitial />}

        {mode === "camera" && (
          <CameraCapture 
            onCapture={handleCapture} 
            onCancel={() => {
              stopCamera();
              setMode("initial");
            }} 
          />
        )}

        {mode === "preview" && capturedImage && (
          <ImagePreview
            imageUrl={capturedImage}
            onRetake={resetCapture}
            onConfirm={confirmImage}
          />
        )}

        {mode === "gallery" && (
          <PhotoGallery 
            onSelectImage={handleSelectFromGallery}
            onClose={() => setMode("initial")}
          />
        )}

        {cameraError && <CameraErrorDisplay errorMessage={cameraError} />}
      </div>

      {(mode === "initial" || (currentCapturedImage && isImageConfirmed)) && (
        <>
          <ImageCaptureActions 
            onUploadClick={handleUploadClick}
            onCameraClick={startCamera}
            onGalleryClick={() => setMode("gallery")}
            capturedImage={currentCapturedImage}
            onGenerateIdeas={onGenerateIdeas}
            isGenerating={isGenerating}
            userCredits={userCredits}
            isImageConfirmed={isImageConfirmed}
          />
          <input 
            ref={fileInputRef}
            type="file" 
            accept="image/*"
            className="hidden" 
            onChange={(e) => handleFileUpload(e, setCapturedImage, setMode)}
          />
        </>
      )}
    </div>
  );
}

export default ImageCapture;
